import Phaser from 'phaser';

const MINIMAP_WIDTH = 120;
const MINIMAP_HEIGHT = 90;
const MINIMAP_MARGIN = 8;
const MINIMAP_UPDATE_INTERVAL = 200; // ms

interface SoldierData {
  name: string;
  hp: number;
  maxHp: number;
  weapon: string;
  isSelected?: boolean;
}

interface EnemyData {
  x: number;
  y: number;
}

export class HUD extends Phaser.Scene {
  // Mini-map
  private minimapBorder!: Phaser.GameObjects.Graphics;
  private minimapDots!: Phaser.GameObjects.Graphics;
  private minimapTimer: number = 0;

  // Troop roster
  private rosterContainer!: Phaser.GameObjects.Container;
  private rosterEntries: {
    nameTxt: Phaser.GameObjects.Text;
    hpBarBg: Phaser.GameObjects.Rectangle;
    hpBarFill: Phaser.GameObjects.Rectangle;
    weaponTxt: Phaser.GameObjects.Text;
  }[] = [];
  private readonly MAX_ROSTER_SLOTS = 4;

  // Weapon / ammo
  private weaponText!: Phaser.GameObjects.Text;
  private ammoText!: Phaser.GameObjects.Text;

  // Kill counter
  private killText!: Phaser.GameObjects.Text;

  // Pause menu
  private pauseOverlay!: Phaser.GameObjects.Container;
  private isPaused: boolean = false;

  // Video recording
  private mediaRecorder: MediaRecorder | null = null;
  private recordingChunks: Blob[] = [];
  private recordBtn: Phaser.GameObjects.Text | null = null;
  private recordTimer: Phaser.Time.TimerEvent | null = null;

  // Previous registry values for dirty-check
  private prevKills: number = -1;
  private prevWeapon: string = '';
  private prevGrenades: number = -1;
  private prevRockets: number = -1;

  constructor() {
    super({ key: 'HUD' });
  }

  create(): void {
    const { width, height } = this.scale;

    // Reset instance state that persists across scene restarts.
    // Phaser reuses the scene class instance — class fields are NOT re-initialised
    // when create() is called again after a stop/launch cycle.
    this.rosterEntries = [];
    this.isPaused = false;
    this.prevKills = -1;
    this.prevWeapon = '';
    this.prevGrenades = -1;
    this.prevRockets = -1;

    this.mediaRecorder = null;
    this.recordingChunks = [];
    this.recordBtn = null;
    this.recordTimer = null;

    this.createKillCounter();
    this.createMinimap(width, height);
    this.createRosterPanel(height);
    this.createWeaponIndicator(width, height);
    this.createPauseMenu(width, height);
    this.createRecordButton(width);

    // ESC → toggle pause (only when HUD scene is active).
    // Remove any previously registered 'down' listeners before adding a new one
    // to prevent accumulation across scene restarts (Phaser reuses Key objects).
    if (this.input.keyboard) {
      const escKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
      escKey.removeAllListeners('down');
      escKey.on('down', () => this.handleEsc());
    }

    // On-screen weapon buttons — only rendered on touch devices
    if (navigator.maxTouchPoints > 0) {
      this.createTouchControls(width, height);
    }

    // If launched from the map editor, show a "← EDITOR" button so the user
    // can return without completing the mission. HUD renders on top of GameScene
    // so the button is always visible.
    if (sessionStorage.getItem('ff_editor_test_mode') === '1') {
      const editorBtn = this.add
        .text(10, height - 30, '← EDITOR', {
          fontSize: '11px',
          color: '#00ff88',
          fontFamily: 'monospace',
          backgroundColor: '#0a0f18cc',
          padding: { x: 6, y: 4 },
        })
        .setDepth(200)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });

      editorBtn.on('pointerdown', () => {
        sessionStorage.removeItem('ff_editor_test_mode');
        this.scene.stop('HUD');
        this.scene.stop('GameScene');
        this.scene.wake('MapEditorScene');
      });
    }

    // Register shutdown cleanup via the scene event system.
    // Phaser fires 'shutdown' on this.events — it does NOT call shutdown() directly.
    this.events.once('shutdown', () => {
      // Stop any in-progress recording so the MediaRecorder is not left open
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.stop();
      }
      // If HUD is stopped while the game is paused, ensure GameScene is resumed
      // so the next run doesn't start in a paused state.
      if (this.isPaused) {
        this.isPaused = false;
        const gameScene = this.scene.get('GameScene');
        if (gameScene) gameScene.scene.resume();
      }
    });
  }

  // ── Kill counter ───────────────────────────────────────────────────────────

  private createKillCounter(): void {
    this.killText = this.add
      .text(8, 8, 'Kills: 0', {
        fontSize: '13px',
        color: '#ff6644',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setDepth(200)
      .setScrollFactor(0);
  }

  // ── Mini-map ───────────────────────────────────────────────────────────────

  private createMinimap(screenW: number, screenH: number): void {
    const mx = screenW - MINIMAP_WIDTH - MINIMAP_MARGIN;
    const my = screenH - MINIMAP_HEIGHT - MINIMAP_MARGIN;

    // Black background
    this.add
      .rectangle(mx, my, MINIMAP_WIDTH, MINIMAP_HEIGHT, 0x000000, 0.75)
      .setOrigin(0, 0)
      .setDepth(195)
      .setScrollFactor(0);

    // Border frame
    this.minimapBorder = this.add.graphics().setDepth(197).setScrollFactor(0);
    this.minimapBorder.lineStyle(1, 0x44ff88, 0.8);
    this.minimapBorder.strokeRect(mx, my, MINIMAP_WIDTH, MINIMAP_HEIGHT);

    // Label
    this.add
      .text(mx + 2, my + 2, 'MAP', {
        fontSize: '8px',
        color: '#44ff88',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setDepth(198)
      .setScrollFactor(0);

    // Dots graphics layer
    this.minimapDots = this.add.graphics().setDepth(196).setScrollFactor(0);
  }

  private updateMinimap(): void {
    const { width: screenW, height: screenH } = this.scale;
    const mx = screenW - MINIMAP_WIDTH - MINIMAP_MARGIN;
    const my = screenH - MINIMAP_HEIGHT - MINIMAP_MARGIN;

    this.minimapDots.clear();

    const soldiers: SoldierData[] = this.registry.get('soldiers') ?? [];
    const enemies: EnemyData[] = this.registry.get('enemies') ?? [];

    // Map world coords → minimap coords
    const worldWidth: number = this.registry.get('worldWidth') ?? 800;
    const worldHeight: number = this.registry.get('worldHeight') ?? 640;
    const scaleX = MINIMAP_WIDTH / worldWidth;
    const scaleY = MINIMAP_HEIGHT / worldHeight;

    for (const s of soldiers) {
      // soldiers have x/y; cast to any since SoldierData doesn't include position
      const sd = s as unknown as { x: number; y: number; hp: number };
      if (sd.hp <= 0) continue;
      const px = mx + sd.x * scaleX;
      const py = my + sd.y * scaleY;
      this.minimapDots.fillStyle(0x00ff44, 1);
      this.minimapDots.fillCircle(px, py, 2);
    }

    for (const e of enemies) {
      const px = mx + e.x * scaleX;
      const py = my + e.y * scaleY;
      this.minimapDots.fillStyle(0xff2222, 1);
      this.minimapDots.fillCircle(px, py, 2);
    }
  }

  // ── Troop roster ───────────────────────────────────────────────────────────

  private createRosterPanel(screenH: number): void {
    const panelX = 8;
    const panelY = screenH - 30 - this.MAX_ROSTER_SLOTS * 28;

    this.rosterContainer = this.add.container(panelX, panelY).setDepth(200).setScrollFactor(0);

    // Panel background
    const panelH = this.MAX_ROSTER_SLOTS * 28 + 6;
    const panelW = 140;
    const bg = this.add.rectangle(0, 0, panelW, panelH, 0x000000, 0.6).setOrigin(0, 0);
    this.rosterContainer.add(bg);

    for (let i = 0; i < this.MAX_ROSTER_SLOTS; i++) {
      const rowY = 4 + i * 28;

      const nameTxt = this.add
        .text(4, rowY, '', {
          fontSize: '10px',
          color: '#cccccc',
          fontFamily: 'monospace',
          resolution: window.devicePixelRatio || 1,
        });

      const hpBarBg = this.add
        .rectangle(4, rowY + 13, 80, 5, 0x440000)
        .setOrigin(0, 0);

      const hpBarFill = this.add
        .rectangle(4, rowY + 13, 80, 5, 0x00cc44)
        .setOrigin(0, 0);

      const weaponTxt = this.add
        .text(90, rowY, '', {
          fontSize: '10px',
          color: '#ffaa44',
          fontFamily: 'monospace',
          resolution: window.devicePixelRatio || 1,
        });

      this.rosterContainer.add([nameTxt, hpBarBg, hpBarFill, weaponTxt]);
      this.rosterEntries.push({ nameTxt, hpBarBg, hpBarFill, weaponTxt });
    }
  }

  private updateRoster(): void {
    const soldiers: SoldierData[] = this.registry.get('soldiers') ?? [];

    for (let i = 0; i < this.MAX_ROSTER_SLOTS; i++) {
      const entry = this.rosterEntries[i];
      const sd = soldiers[i] as (SoldierData & { x?: number; y?: number }) | undefined;

      if (!sd) {
        entry.nameTxt.setText('');
        entry.hpBarBg.setVisible(false);
        entry.hpBarFill.setVisible(false);
        entry.weaponTxt.setText('');
        continue;
      }

      const name = (sd.name || '?').substring(0, 10);
      // Highlight selected soldiers with a ▶ prefix
      entry.nameTxt.setText(sd.isSelected ? `▶${name}` : ` ${name}`);
      entry.nameTxt.setColor(sd.isSelected ? '#00ff88' : '#cccccc');
      entry.hpBarBg.setVisible(true);
      entry.hpBarFill.setVisible(true);

      const ratio = Math.max(0, Math.min(1, sd.hp / sd.maxHp));
      entry.hpBarFill.setSize(80 * ratio, 5);

      // Health bar color: green → yellow → red
      let barColor = 0x00cc44;
      if (ratio < 0.5) barColor = 0xffcc00;
      if (ratio < 0.25) barColor = 0xff2222;
      entry.hpBarFill.setFillStyle(barColor);

      // Weapon label directly from per-soldier inventory (e.g. "MG", "GRN x3", "RKT x2")
      entry.weaponTxt.setText(sd.weapon ?? '');
    }
  }

  // ── Weapon / ammo indicator ────────────────────────────────────────────────

  private createWeaponIndicator(screenW: number, screenH: number): void {
    const cx = screenW / 2;
    const by = screenH - 8;

    this.weaponText = this.add
      .text(cx, by - 18, 'MACHINE GUN', {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 1)
      .setDepth(200)
      .setScrollFactor(0);

    this.ammoText = this.add
      .text(cx, by, '', {
        fontSize: '12px',
        color: '#ffcc44',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 1)
      .setDepth(200)
      .setScrollFactor(0);
  }

  private updateWeaponIndicator(): void {
    const weapon: string = this.registry.get('currentWeapon') ?? 'MACHINE GUN';
    const grenades: number = this.registry.get('grenades') ?? 0;
    const rockets: number = this.registry.get('rockets') ?? 0;

    if (
      weapon === this.prevWeapon &&
      grenades === this.prevGrenades &&
      rockets === this.prevRockets
    ) return;

    this.prevWeapon = weapon;
    this.prevGrenades = grenades;
    this.prevRockets = rockets;

    this.weaponText.setText(weapon);

    // Always show ammo counts for active soldier
    const parts: string[] = [];
    parts.push(`💣 x${grenades}`);
    parts.push(`🚀 x${rockets}`);
    this.ammoText.setText(parts.join('  '));
  }

  // ── Touch controls ─────────────────────────────────────────────────────────

  private createTouchControls(screenW: number, screenH: number): void {
    const buttons: { label: string; cmd: string; color: number }[] = [
      { label: 'CYCLE', cmd: 'cycle', color: 0x4488ff },
      { label: 'GRENADE', cmd: 'grenade', color: 0xff8800 },
      { label: 'ROCKET', cmd: 'rocket', color: 0xff4400 },
      { label: 'CANCEL', cmd: 'cancel', color: 0x888888 },
    ];

    const btnW = 100;
    const btnH = 44;
    const gap = 8;
    const totalW = buttons.length * (btnW + gap) - gap;
    const startX = (screenW - totalW) / 2;
    const y = screenH - btnH / 2 - 8;

    buttons.forEach((def, i) => {
      const x = startX + i * (btnW + gap) + btnW / 2;

      const bg = this.add
        .rectangle(x, y, btnW, btnH, 0x0d1b2a)
        .setStrokeStyle(2, def.color)
        .setDepth(200)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });

      this.add
        .text(x, y, def.label, {
          fontSize: '11px',
          color: `#${def.color.toString(16).padStart(6, '0')}`,
          fontFamily: 'monospace',
          resolution: window.devicePixelRatio || 1,
        })
        .setOrigin(0.5, 0.5)
        .setDepth(201)
        .setScrollFactor(0);

      bg.on('pointerover', () => bg.setFillStyle(def.color, 0.3));
      bg.on('pointerout', () => bg.setFillStyle(0x0d1b2a));
      bg.on('pointerdown', () => {
        const gameScene = this.scene.get('GameScene');
        if (gameScene) gameScene.events.emit('touchCommand', def.cmd);
      });
    });
  }

  // ── Pause menu ─────────────────────────────────────────────────────────────

  private createPauseMenu(screenW: number, screenH: number): void {
    this.pauseOverlay = this.add.container(0, 0).setDepth(300).setScrollFactor(0).setVisible(false);

    // Semi-transparent dark overlay
    const overlay = this.add.rectangle(0, 0, screenW, screenH, 0x000000, 0.65).setOrigin(0, 0);
    this.pauseOverlay.add(overlay);

    // Pause label
    const pauseLabel = this.add
      .text(screenW / 2, screenH / 2 - 90, '⏸  PAUSED', {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 4,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);
    this.pauseOverlay.add(pauseLabel);

    // Buttons
    const buttonDefs = [
      { label: 'RESUME', y: screenH / 2 - 20, action: () => this.resumeGame() },
      { label: 'RETRY MISSION', y: screenH / 2 + 40, action: () => this.retryMission() },
      { label: 'QUIT TO BRIEFING', y: screenH / 2 + 100, action: () => this.quitToBriefing() },
    ];

    for (const def of buttonDefs) {
      const btn = this.createPauseButton(screenW / 2, def.y, def.label, def.action);
      this.pauseOverlay.add(btn);
    }
  }

  private createPauseButton(
    x: number,
    y: number,
    label: string,
    action: () => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add
      .rectangle(0, 0, 220, 40, 0x112233)
      .setStrokeStyle(2, 0x4488ff)
      .setInteractive({ useHandCursor: true });

    const txt = this.add
      .text(0, 0, label, {
        fontSize: '14px',
        color: '#88bbff',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    bg.on('pointerover', () => { bg.setFillStyle(0x4488ff); txt.setColor('#000000'); });
    bg.on('pointerout', () => { bg.setFillStyle(0x112233); txt.setColor('#88bbff'); });
    bg.on('pointerdown', action);

    container.add([bg, txt]);
    return container;
  }

  private handleEsc(): void {
    // Only handle ESC if GameScene is in normal mode (no targeting)
    const isTargeting: boolean = this.registry.get('targetingMode') ?? false;
    if (isTargeting) return;

    this.togglePause();
  }

  togglePause(): void {
    this.isPaused = !this.isPaused;
    this.pauseOverlay.setVisible(this.isPaused);

    const gameScene = this.scene.get('GameScene');
    if (gameScene) {
      if (this.isPaused) {
        gameScene.scene.pause();
      } else {
        gameScene.scene.resume();
      }
    }
  }

  private resumeGame(): void {
    if (this.isPaused) this.togglePause();
  }

  private retryMission(): void {
    this.isPaused = false;
    this.pauseOverlay.setVisible(false);
    const gameScene = this.scene.get('GameScene');
    if (gameScene) gameScene.scene.resume();
    this.scene.stop('HUD');
    this.scene.start('GameScene');
  }

  private quitToBriefing(): void {
    this.isPaused = false;
    this.pauseOverlay.setVisible(false);
    const gameScene = this.scene.get('GameScene');
    if (gameScene) gameScene.scene.resume();
    this.scene.stop('GameScene');
    this.scene.stop('HUD');
    this.scene.start('BriefingScene');
  }

  // ── Video recording ────────────────────────────────────────────────────────

  private createRecordButton(screenW: number): void {
    // canvas.captureStream is not available on iOS Safari — hide button entirely
    if (typeof (this.game.canvas as HTMLCanvasElement & { captureStream?: () => MediaStream }).captureStream !== 'function') {
      return;
    }

    this.recordBtn = this.add
      .text(screenW - 8, 8, '⏺ REC', {
        fontSize: '12px',
        color: '#ff4444',
        fontFamily: 'monospace',
        backgroundColor: '#0a0f18cc',
        padding: { x: 6, y: 4 },
      })
      .setOrigin(1, 0)
      .setDepth(200)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });

    this.recordBtn.on('pointerdown', () => {
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    });
  }

  private startRecording(): void {
    const canvas = this.game.canvas as HTMLCanvasElement & { captureStream: (fps: number) => MediaStream };
    const stream = canvas.captureStream(30);

    // Prefer webm/vp9 where available
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm';

    this.recordingChunks = [];
    this.mediaRecorder = new MediaRecorder(stream, { mimeType });
    this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
      if (e.data.size > 0) this.recordingChunks.push(e.data);
    };
    this.mediaRecorder.onstop = () => this.downloadRecording();
    this.mediaRecorder.start(100); // collect chunks every 100 ms

    if (this.recordBtn) {
      this.recordBtn.setText('⏹ 30s');
      this.recordBtn.setColor('#ff0000');
    }

    // Auto-stop after 30 seconds
    this.recordTimer = this.time.addEvent({
      delay: 30000,
      callback: () => this.stopRecording(),
    });
  }

  private stopRecording(): void {
    if (this.recordTimer) {
      this.recordTimer.remove();
      this.recordTimer = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    if (this.recordBtn) {
      this.recordBtn.setText('⏺ REC');
      this.recordBtn.setColor('#ff4444');
    }
  }

  private downloadRecording(): void {
    const blob = new Blob(this.recordingChunks, { type: 'video/webm' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'warboys-gameplay.webm';
    a.click();
    URL.revokeObjectURL(url);
    this.recordingChunks = [];
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  update(_time: number, delta: number): void {
    if (this.isPaused) return;

    // Kill counter
    const kills: number = this.registry.get('kills') ?? 0;
    if (kills !== this.prevKills) {
      this.prevKills = kills;
      this.killText.setText(`Kills: ${kills}`);
    }

    // Minimap — throttled
    this.minimapTimer -= delta;
    if (this.minimapTimer <= 0) {
      this.minimapTimer = MINIMAP_UPDATE_INTERVAL;
      this.updateMinimap();
    }

    // Roster — every frame
    this.updateRoster();

    // Weapon / ammo
    this.updateWeaponIndicator();
  }
}
